import { computed, ref } from "vue";
import { isTest } from "@/config";

// Dev and test only: keeps one flyout open to work on its panel, e.g. `localStorage.menu_keep_open = "Football"` (the
// label as shown in the menu), then reload. Remove the key to get the normal hover behaviour back.
const keepOpenLabel = ((import.meta.env.DEV || isTest) && globalThis.localStorage?.getItem("menu_keep_open")) || null;

// The main menu flyout that is open, shared by all nav items: moving from one item to the next swaps the panel in place
// instead of closing one and opening the other, which would flash the backdrop.
const openFlyout = ref<string | null>(keepOpenLabel);
// True while moving from one open flyout to the next: that swap skips the open/close animation.
const isSwitching = ref(false);
// Height of the open panel's sheet: a panel swapped in starts at it and grows or shrinks to its own height.
const lastSheetHeight = ref<number | null>(null);
// The nav item just clicked to go to its page: its flyout doesn't open on hover until the pointer has left it, so it
// doesn't pop open over the page it just opened while the pointer is still on it.
let suppressedLabel: string | null = null;

// Hovering waits before it opens a flyout, so the pointer can cross the nav row (e.g. from the logo down to the page)
// without opening one. Moving to another item while one is open waits too, so passing over a neighbouring item on the
// way into the open panel doesn't swap it. Like apple.com, the whole nav row (items, the gaps between them and the open
// panel) keeps a flyout open: only leaving the row starts the close, which waits so a quick slip out doesn't close it.
export const OPEN_DELAY = 250;
export const SWITCH_DELAY = 150;
const CLOSE_DELAY = 200;

// Shared by all nav items. A pending open or switch (to another item, or closing for an item without a flyout) and a
// pending close are separate, so coming back into the row drops the close but keeps a switch to the item under the pointer.
let openTimer: ReturnType<typeof setTimeout> | undefined;
let closeTimer: ReturnType<typeof setTimeout> | undefined;
const cancelOpen = () => clearTimeout(openTimer);
const cancelClose = () => clearTimeout(closeTimer);
const cancelHover = () => {
  cancelOpen();
  cancelClose();
};

export function useMegaMenuOpen() {
  const openFlyoutFor = (label: string) => {
    cancelHover();
    isSwitching.value = openFlyout.value !== null && openFlyout.value !== label;
    openFlyout.value = label;
  };

  const closeFlyout = (label: string) => {
    if (openFlyout.value !== label) return;
    cancelHover();
    isSwitching.value = false;
    openFlyout.value = keepOpenLabel;
  };

  const resetFlyout = () => {
    cancelHover();
    isSwitching.value = false;
    openFlyout.value = keepOpenLabel;
  };

  // A click on a nav item that goes to its page: close its flyout, and keep it closed while the pointer stays on it.
  const suppressFlyout = (label: string) => {
    suppressedLabel = label;
    closeFlyout(label);
    cancelOpen();
  };

  // The pointer left a nav item: hovering it again opens its flyout again.
  const leaveFlyoutItem = (label: string) => {
    if (suppressedLabel === label) suppressedLabel = null;
  };

  // `null` is a nav item without a flyout: hovering it closes the open one, after the same delay as a switch.
  const hoverFlyout = (label: string | null) => {
    if (label !== null && label === suppressedLabel) return;
    cancelHover();
    const open = openFlyout.value;
    if (open === label) return;
    if (label === null) {
      if (open !== null) openTimer = setTimeout(() => closeFlyout(open), SWITCH_DELAY);
      return;
    }
    openTimer = setTimeout(() => openFlyoutFor(label), open === null ? OPEN_DELAY : SWITCH_DELAY);
  };

  // The pointer is on the open panel: keep it, dropping a pending close or a switch to an item passed on the way.
  const keepFlyout = () => cancelHover();

  // The pointer is anywhere in the nav row, e.g. a gap between items: keep the open flyout.
  const stayInNavRow = () => cancelClose();

  // The pointer left the nav row (and with it the open panel, which sits inside the row).
  const leaveNavRow = () => {
    suppressedLabel = null;
    cancelOpen();
    const label = openFlyout.value;
    if (label !== null) closeTimer = setTimeout(() => closeFlyout(label), CLOSE_DELAY);
  };

  const isMegaMenuOpen = computed(() => openFlyout.value !== null);

  return {
    openFlyout,
    isSwitching,
    lastSheetHeight,
    isMegaMenuOpen,
    openFlyoutFor,
    closeFlyout,
    resetFlyout,
    hoverFlyout,
    suppressFlyout,
    leaveFlyoutItem,
    keepFlyout,
    stayInNavRow,
    leaveNavRow,
  };
}
